/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

// tslint:disable:variable-name

import { arrayOf, constantMapper, date, DateFormat, entity, Enum, enumeration, subentity, enumFromDictionary } from '@gh/core-data';
import { CommunicationListItemMapper } from '../../communications/communication.mapper';
import { CustomFieldPlacement, CustomFieldType } from '../../custom-fields';
import { MailRequirementSetting } from '../../system-vars/mail-requirement-setting.enum';
import { ContactDetailsCityCountyDto } from '../model/contact-details-city-county.dto';
import {
  ContactDetailsCommonDto,
  ContactDetailsTypeDto,
  ContactDisplay,
  ContactDuplicateFilterDto,
  ContactDuplicateItemDto,
  ContactDuplicateSearchMode,
  ContactRoleType,
} from '../model/contact-details-common.dto';
import { ContactDetailsCommunicationDataDto } from '../model/contact-details-communication.dto';
import {
  BusinessNumberType,
  ContactDetailsCreditorBusinessInfoDto,
  ContactDetailsCreditorCommonDto,
  ContactDetailsCreditorContractDto,
  ContactDetailsCreditorDeliveryInfoDto,
  ContactDetailsCreditorDto,
  ContactDetailsCreditorInsuranceDto,
  ContactDetailsCreditorLiabilityDto,
  ContactDetailsCreditorSubContractorDto,
  ContactDetailsCreditorTrackingDto,
  CreditorType,
  EmailMatch,
  SupplierCategoryType,
  SupplierServiceType,
  TrackingCaption,
  WithholdingTaxType,
} from '../model/contact-details-creditor.dto';
import {
  ContactDetailsCustomerAdditionalDetailsDto,
  ContactDetailsCustomerBillingDto,
  ContactDetailsCustomerBirthdaysDto,
  ContactDetailsCustomerCommonDto,
  ContactDetailsCustomerDto,
  ContactDetailsCustomerKidDto,
  ContactDetailsInvitationSubTabDto,
  ContactInvitationStatus,
} from '../model/contact-details-customer.dto';
import {
  ContactDetailsDebtorBillingDto,
  ContactDetailsDebtorCommonDto,
  ContactDetailsDebtorDto,
} from '../model/contact-details-debtor.dto';
import {
  ContactDetailsEmployeeCommonDto,
  ContactDetailsEmployeeDto,
  ContactDetailsEmployeePersonalDto,
  ContactDetailsEmployeeSalaryDto,
} from '../model/contact-details-employee.dto';
import {
  ContactDetailsFinanceCompanyBillingDto,
  ContactDetailsFinanceCompanyCommonDto,
  ContactDetailsFinanceCompanyDto,
} from '../model/contact-details-finance-company.dto';
import {
  ContactDeliveryMethod,
  ContactDetailsCustomFieldItemDto,
  ContactDetailsCustomFieldListDto,
  ContactDetailsPaymentCreditorDto,
  ContactDetailsPaymentPayeeDto,
  ContactDetailsPaymentPayerDto,
  ContactDetailsTermsPayeeDto,
  ContactDetailsTermsPayerDto,
  ContactPayBy,
  ContactTermsPayables,
  ContactTermsReceivables,
} from '../model/contact-details-shared.dto';
import { ContactDetailsDto } from '../model/contact-details.dto';
import { ContactDetailsOtherDto } from '../model/contact-other.dto';
import { ContactType } from '../model/contact-type.enum';

export const ContactDuplicateItemMapper = entity<ContactDuplicateItemDto>();

export const ContactDeliveryMethodIntMapper = constantMapper({
  1: ContactDeliveryMethod.Email,
  2: ContactDeliveryMethod.Mail,
});

export const ContactDuplicateFilterMapper = entity<ContactDuplicateFilterDto>({
  mode: enumeration(ContactDuplicateSearchMode),
});

const ContactCreditorDeliveryInfoMapper = subentity<ContactDetailsCreditorDeliveryInfoDto>({
  deliveryMethod: enumeration(ContactDeliveryMethod),
  emailMatch: enumeration(EmailMatch),
});

const ContactCustomFieldListMapper = subentity<ContactDetailsCustomFieldListDto>({
  data: arrayOf(subentity<ContactDetailsCustomFieldItemDto>({
    customFieldPlacement: enumeration(CustomFieldPlacement),
    dataType: enumeration(CustomFieldType),
    dateValue: date(DateFormat.Date),
  })),
});

const ContactPaymentPayer = subentity<ContactDetailsPaymentPayerDto>({
  payBy: enumeration(ContactPayBy),
});

const ContactPaymentPayee = (paidBy: Enum<any>) => subentity<ContactDetailsPaymentPayeeDto>({
  // @ts-ignore
  paidBy: enumFromDictionary(paidBy),
});

const ContactDetailsPaymentCreditor = (paidBy: Enum<any>) => subentity<ContactDetailsPaymentCreditorDto>({
  // @ts-ignore
  paidBy: enumFromDictionary(paidBy),
});

const ContactTermsPayer = subentity<ContactDetailsTermsPayerDto>({
  paymentIsDue: enumeration(ContactTermsReceivables),
});

const ContactTermsPayee = subentity<ContactDetailsTermsPayeeDto>({
  paymentIsDue: enumeration(ContactTermsPayables),
});

export const ContactDetailsMapper = (mappers: any) => {
  const {gender, paidBy, payBasis, payRoll} = mappers;
  return entity<ContactDetailsDto>({
  common: subentity<ContactDetailsCommonDto>({
    type: enumeration(ContactRoleType),
    displayNameAs: enumeration(ContactDisplay),
    mailRequirementSetting: enumeration(MailRequirementSetting),
    contactTypes: arrayOf(subentity<ContactDetailsTypeDto>({
      type: enumeration(ContactType),
    })),
  }),

  employee: subentity<ContactDetailsEmployeeDto>({
    common: subentity<ContactDetailsEmployeeCommonDto>({
      salary: subentity<ContactDetailsEmployeeSalaryDto>({
        // @ts-ignore
        payrollCycle: enumFromDictionary(payRoll),
        // @ts-ignore
        payBasis: enumFromDictionary(payBasis),
      }),
      personal: subentity<ContactDetailsEmployeePersonalDto>({
        // @ts-ignore
        gender: enumFromDictionary(gender),
        dateOfBirth: date(DateFormat.Date),
        startDate: date(DateFormat.Date),
        terminationDate: date(DateFormat.Date),
      }),
    }),
    payment: ContactPaymentPayee(paidBy),
    terms: ContactTermsPayee,
    communication: subentity<ContactDetailsCommunicationDataDto>({
      communications: arrayOf(CommunicationListItemMapper),
    }),
    customFields: ContactCustomFieldListMapper,
  }),

  creditorSupplier: subentity<ContactDetailsCreditorDto>({
    common: subentity<ContactDetailsCreditorCommonDto>({
      type: enumeration(CreditorType),
      businessInfo: subentity<ContactDetailsCreditorBusinessInfoDto>({
        numberType: enumeration(BusinessNumberType),
        type: enumeration(WithholdingTaxType),
        certificateDate: date(DateFormat.Date),
        licenceExpired: date(DateFormat.Date),
      }),
      orderBy: ContactCreditorDeliveryInfoMapper,
      billingBy: ContactCreditorDeliveryInfoMapper,
      scheduleBy: ContactCreditorDeliveryInfoMapper,
      provides: enumeration(SupplierServiceType),
      category: enumeration(SupplierCategoryType),
    }),
    subContractor: subentity<ContactDetailsCreditorSubContractorDto>({
      contract: subentity<ContactDetailsCreditorContractDto>({
        contractStart: date(DateFormat.Date),
        contractEnd: date(DateFormat.Date),
        contractSent: date(DateFormat.Date),
        contractReceived: date(DateFormat.Date),
      }),
      insurance: subentity<ContactDetailsCreditorInsuranceDto>({
        expired: date(DateFormat.Date),
      }),
      liability: subentity<ContactDetailsCreditorLiabilityDto>({
        expired: date(DateFormat.Date),
      }),
      tracking: arrayOf(subentity<ContactDetailsCreditorTrackingDto>({
        value: enumeration(TrackingCaption),
      })),
    }),
    terms: ContactTermsPayee,
    customFields: ContactCustomFieldListMapper,
    communication: subentity<ContactDetailsCommunicationDataDto>({
      communications: arrayOf(CommunicationListItemMapper),
    }),
    invitations: arrayOf(subentity<ContactDetailsInvitationSubTabDto>({
      invitationStatus: enumeration(ContactInvitationStatus),
    })),
  }),

  customer: subentity<ContactDetailsCustomerDto>({
    common: subentity<ContactDetailsCustomerCommonDto>({
      billing: subentity<ContactDetailsCustomerBillingDto>({
        deliveryBy: enumeration(ContactDeliveryMethod),
      }),
    }),
    payment: ContactPaymentPayer,
    terms: ContactTermsPayer,
    communication: subentity<ContactDetailsCommunicationDataDto>({
      communications: arrayOf(CommunicationListItemMapper),
    }),
    customFields: ContactCustomFieldListMapper,
    invitations: arrayOf(subentity<ContactDetailsInvitationSubTabDto>({
      invitationStatus: enumeration(ContactInvitationStatus),
    })),
    additionalDetails: subentity<ContactDetailsCustomerAdditionalDetailsDto>({
      dateOfBirth: date(DateFormat.Date),
      additionalDatesOfBirth: subentity<ContactDetailsCustomerBirthdaysDto>({
        partnerDateOfBirth: date(DateFormat.Date),
        parent1DateOfBirth: date(DateFormat.Date),
        parent2DateOfBirth: date(DateFormat.Date),
        kids: arrayOf(subentity<ContactDetailsCustomerKidDto>({
          dateOfBirth: date(DateFormat.Date),
        })),
      }),
    }),
  }),

  cityCounty: subentity<ContactDetailsCityCountyDto>({
    communication: subentity<ContactDetailsCommunicationDataDto>({
      communications: arrayOf(CommunicationListItemMapper),
    }),
    customFields: ContactCustomFieldListMapper,
  }),

  financeCompany: subentity<ContactDetailsFinanceCompanyDto>({
    common: subentity<ContactDetailsFinanceCompanyCommonDto>({
      billing: subentity<ContactDetailsFinanceCompanyBillingDto>({
        deliveryBy: enumeration(ContactDeliveryMethod),
      }),
    }),
    payment: ContactPaymentPayer,
    terms: ContactTermsPayer,
    communication: subentity<ContactDetailsCommunicationDataDto>({
      communications: arrayOf(CommunicationListItemMapper),
    }),
    customFields: ContactCustomFieldListMapper,
  }),

  debtor: subentity<ContactDetailsDebtorDto>({
    common: subentity<ContactDetailsDebtorCommonDto>({
      billing: subentity<ContactDetailsDebtorBillingDto>({
        deliveryBy: enumeration(ContactDeliveryMethod),
      }),
    }),
    payment: ContactPaymentPayer,
    terms: ContactTermsPayer,
    communication: subentity<ContactDetailsCommunicationDataDto>({
      communications: arrayOf(CommunicationListItemMapper),
    }),
    customFields: ContactCustomFieldListMapper,
  }),

  other: subentity<ContactDetailsOtherDto>({
    communication: subentity<ContactDetailsCommunicationDataDto>({
      communications: arrayOf(CommunicationListItemMapper),
    }),
    customFields: ContactCustomFieldListMapper,
  }),
}); };
